import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, categoryColorMap } from '@/mocks/categories';
import { getArticlesByCategory, articles } from '@/mocks/articles';

const categoryNameMap: Record<string, string> = {
  'wanghong-tafang': '网红塌房',
  'heiliao-baoliao': '黑料爆料',
  'meiri-dasai': '每日大赛',
  'yule-bagua': '娱乐八卦',
  'zhibo-chigua': '直播吃瓜',
  'shipin-remen': '视频热门',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const categoryName = categoryNameMap[slug || ''] || '分类';
  const categoryArticles = getArticlesByCategory(slug || '');
  const catInfo = categories.find((c) => c.slug === slug);
  const colors = catInfo ? categoryColorMap[catInfo.color] : categoryColorMap.primary;

  useEffect(() => {
    document.title = `${categoryName} | 吃瓜网站线路一 | 51吃瓜网站入口`;
  }, [categoryName]);

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          'name': `${categoryName} - 吃瓜网站线路一`,
          'description': `浏览${categoryName}相关的所有吃瓜内容，51吃瓜网站入口提供最新${categoryName}资讯`,
          'url': `http://91blw.cn/category/${slug}`,
          'breadcrumb': {
            '@type': 'BreadcrumbList',
            'itemListElement': [
              { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'http://91blw.cn/' },
              { '@type': 'ListItem', 'position': 2, 'name': categoryName, 'item': `http://91blw.cn/category/${slug}` },
            ],
          },
        })}
      </script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Banner */}
        <section className="relative w-full h-[280px] md:h-[380px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Chinese%20entertainment%20news%20abstract%20background%20warm%20peach%20and%20coral%20gradient%20soft%20geometric%20shapes%20clean%20minimal%20aesthetic%20editorial&width=1600&height=900&seq=cat-hero-generic&orientation=landscape"
            alt={categoryName}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
          <div className="relative z-10 text-center px-4">
            <h1 className="font-heading text-3xl md:text-5xl text-background-50 font-bold mb-4">
              {categoryName}
            </h1>
            <p className="text-sm md:text-base text-background-50/70 max-w-xl mx-auto">
              {catInfo?.description || `浏览${categoryName}相关的所有吃瓜内容`}
            </p>
          </div>
        </section>

        {/* Breadcrumb */}
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 py-3">
            <nav className="flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors duration-200">首页</Link>
              <i className="ri-arrow-right-s-line text-foreground-300"></i>
              <span className="text-foreground-800 font-medium">{categoryName}</span>
            </nav>
          </div>
        </div>

        {/* Articles Grid */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
              <div>
                <p className="text-foreground-400 text-sm tracking-wide mb-2">分类浏览</p>
                <h2 className="font-heading text-3xl md:text-4xl text-foreground-950 font-bold">
                  {categoryName}
                </h2>
              </div>
              <p className="text-foreground-500 text-sm max-w-md leading-relaxed">
                共 {categoryArticles.length} 篇相关内容，持续更新中
              </p>
            </div>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group cursor-pointer bg-background-100 rounded-2xl overflow-hidden hover:bg-background-200/70 transition-all duration-300"
                  >
                    <div className="relative w-full h-48 md:h-52 overflow-hidden">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        title={article.title}
                      />
                      {article.isExclusive && (
                        <span className="absolute top-3 left-3 bg-accent-500 text-background-50 text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap">
                          独家
                        </span>
                      )}
                      {article.isHot && (
                        <span className="absolute top-3 right-3 bg-primary-500 text-background-50 text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap">
                          <i className="ri-fire-line mr-1"></i>热门
                        </span>
                      )}
                    </div>
                    <div className="p-4 md:p-5">
                      <span className="inline-block bg-secondary-100 text-secondary-800 text-xs px-2.5 py-1 rounded-full mb-3 whitespace-nowrap">
                        {article.category}
                      </span>
                      <h3 className="font-heading text-base md:text-lg text-foreground-900 font-bold mb-2 line-clamp-2 leading-snug">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-4">
                        {article.summary}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <span>{article.publishDate}</span>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i> {article.readCount.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className={`w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full ${colors.badge}`}>
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无内容，敬请期待</p>
              </div>
            )}
          </div>
        </section>

        {/* Sub-category feature section */}
        <section className="py-10 md:py-14 bg-secondary-50">
          <div className="w-full px-4 md:px-6">
            <div className="mb-8">
              <p className="text-foreground-400 text-sm tracking-wide mb-2">探索更多</p>
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 font-bold">
                其他热门分类
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.filter((c) => c.slug !== slug).slice(0, 3).map((cat) => {
                const catColors = categoryColorMap[cat.color];
                return (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="bg-background-50 rounded-lg p-5 hover:bg-background-100 transition-all duration-300 cursor-pointer group"
                  >
                    <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${catColors.badge} ${catColors.badgeText} mb-3`}>
                      <i className={`${cat.icon} text-lg`}></i>
                    </div>
                    <h3 className="font-heading text-base text-foreground-900 font-bold mb-1">{cat.name}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{cat.description}</p>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}